import React, { useEffect, useRef, useState } from 'react'
import { Trash,
  UserCircle,
  Envelope,
  Sparkle,
  PuzzlePiece,
  // ChatCircleDots,  // restore with the hidden Auto-Reply Rules nav item
  // SlidersHorizontal,  // restore with the hidden Campaign Defaults nav item
  ShieldCheck,
  Users,
  UsersThree,
  ClipboardText,
  CheckSquare,
  PaperPlaneTilt,
  UploadSimple,
  MagnifyingGlass,
  Question,
  Signature,
  HourglassMedium,
  Lifebuoy,
  type Icon,
} from '@phosphor-icons/react'
import { cn } from '@/lib/utils'
import { usePermission } from '@/hooks/usePermission'
import { useAuthStore } from '@/stores/authStore'
import type { SettingsSection } from '@/types/settings'

interface SettingsNavProps {
  activeSection: SettingsSection
  /** `anchor` is a `data-settings-anchor` slug to scroll to once the section mounts. */
  onSectionChange: (section: SettingsSection, anchor?: string) => void
}

const navItems: { id: SettingsSection; label: string; icon: Icon }[] = [
  { id: 'account', label: 'Account & Profile', icon: UserCircle },
  // Labelled "Mailboxes" in the UI; the id stays `email-accounts` because the
  // store, deep-link anchors and existing sessions all key on it.
  { id: 'email-accounts', label: 'Mailboxes', icon: Envelope },
  // The account-level signature library (Settings > Signatures). Gated on
  // signatures.manage below; the plan lock is rendered inside the section.
  { id: 'signatures', label: 'Signatures', icon: Signature },
  { id: 'ai-models', label: 'AI & Models', icon: Sparkle },
  { id: 'integrations', label: 'Integrations', icon: PuzzlePiece },
  // Auto-Reply Rules — hidden (product decision). The section component,
  // its permission gate and the backend rules engine are all untouched;
  // restore this line (plus the ChatCircleDots import, the 'auto-reply'
  // SECTION_KEYWORDS block, and the SettingsPage route entry) to bring back.
  // { id: 'auto-reply', label: 'Auto-Reply Rules', icon: ChatCircleDots },
  // Campaign Defaults — hidden (product decision). The section component, the
  // settingsStore actions and the backend endpoints are all untouched;
  // restore this line (plus the SlidersHorizontal import, the
  // 'campaign-defaults' SECTION_KEYWORDS block, and the SettingsPage route
  // entry) to bring it back.
  // { id: 'campaign-defaults', label: 'Campaign Defaults', icon: SlidersHorizontal },
  { id: 'security', label: 'Security', icon: ShieldCheck },
  // Custom AI Recipes — hidden (product decision). The section component, the
  // settingsStore recipe actions and the backend endpoints are all untouched;
  // restore this line (plus the Flask import, the 'custom-ai-recipes'
  // SECTION_KEYWORDS block, and the SettingsPage route entry) to bring back.
  // { id: 'custom-ai-recipes', label: 'Custom AI Recipes', icon: Flask },
  { id: 'team', label: 'Team', icon: Users },
  // Read-only for every role, no usePermission gate: it describes what each
  // role can do, which is meaningful to see regardless of your own role.
  { id: 'roles-access', label: 'Roles & Access', icon: UsersThree },
  { id: 'workflow-approvals', label: 'Workflow Approvals', icon: CheckSquare },
  { id: 'campaign-approvals', label: 'Campaign Approvals', icon: PaperPlaneTilt },
  { id: 'crm-export-approvals', label: 'CRM Export Approvals', icon: UploadSimple },
  { id: 'deletion-approvals', label: 'Deletion Approvals', icon: Trash },
  // A Representative's own view of the approvals they asked for. Keyed on
  // the role, not a permission: every other role decides, never requests.
  { id: 'my-approval-requests', label: 'My Approval Requests', icon: HourglassMedium },
  { id: 'audit-log', label: 'Audit Log', icon: ClipboardText },
  // Help is not a permission: every role gets the FAQ and the guide videos.
  { id: 'help-centre', label: 'Help Centre', icon: Question },
  // Feedback & support is not a permission either: every role can reach the
  // team, same reasoning as Help Centre above it.
  { id: 'feedback-support', label: 'Feedback & support', icon: Lifebuoy },
]

interface SectionKeyword {
  /** What search matches against and shows in the result row. */
  label: string
  /**
   * Deep-link target: a stable slug `SettingsPage` resolves inside the mounted
   * panel against `[data-settings-anchor="<slug>"]`, falling back to an element
   * `id` of the same name. Deliberately not the visible copy — copy changes,
   * anchors must not.
   *
   * A keyword with no `anchor` still routes to its section; the panel just
   * opens at the top. That is the honest state for a field whose only control
   * lives in a modal (SMTP/IMAP below) — better than pointing the flash at
   * something that is not the field.
   */
  anchor?: string
}

/** Sugar so the table below stays one keyword per line. */
const kw = (label: string, anchor?: string): SectionKeyword => ({ label, anchor })

// Deeper search index — fields/sub-items inside each section so search can
// surface e.g. "Avatar" or "Dark mode" and route to the right section.
const sectionKeywords: Record<SettingsSection, SectionKeyword[]> = {
  account: [
    kw('Full name', 'account-full-name'),
    kw('Email', 'account-email'),
    kw('Username', 'account-username'),
    kw('Company name', 'account-company-name'),
    kw('Website', 'account-company-website'),
    kw('Description', 'account-company-description'),
    kw('Address', 'account-company-address'),
    kw('Phone', 'account-phone'),
    kw('Logo', 'account-logo'),
    kw('Brand colors', 'account-brand-colors'),
    kw('Brand book', 'account-brand-book'),
    kw('Appearance', 'account-appearance'),
    kw('Theme', 'account-appearance'),
    kw('Light mode', 'account-appearance'),
    kw('Dark mode', 'account-appearance'),
  ],
  // 'SMTP' and 'IMAP' stay anchorless on purpose: those fields live in
  // Add/EditSenderEmailModal, so there is nothing in the mounted panel to scroll
  // to. They still route to the section that opens those modals.
  'email-accounts': [
    kw('Sender email', 'email-accounts-sender-email'),
    kw('SMTP'),
    kw('IMAP'),
    kw('Daily send limit', 'email-accounts-daily-send-limit'),
    kw('Warmup', 'email-accounts-warmup'),
    kw('Email signature', 'email-accounts-email-signature'),
    // One anchor for the four DNS keywords — the individual SPF/DKIM/DMARC
    // pills are rendered by DnsStatusBadge, so the row that holds them is the
    // finest target available here.
    kw('DNS', 'email-accounts-dns'),
    kw('SPF', 'email-accounts-dns'),
    kw('DKIM', 'email-accounts-dns'),
    kw('DMARC', 'email-accounts-dns'),
    // The verification verdict itself — the Verify action lives in the row's
    // overflow menu, which only exists while it is open.
    kw('Verify connection', 'email-accounts-verify-connection'),
  ],
  signatures: [
    kw('Signature'),
    kw('Team signature'),
    kw('Shared signature'),
    kw('Email signature'),
  ],
  'ai-models': [
    kw('OpenAI', 'ai-models-provider'),
    kw('Anthropic', 'ai-models-provider'),
    kw('Claude', 'ai-models-provider'),
    kw('GPT', 'ai-models-provider'),
    kw('LLM provider', 'ai-models-provider'),
    kw('Model', 'ai-models-model'),
    kw('API key', 'ai-models-api-key'),
    kw('Preferred provider', 'ai-models-provider'),
  ],
  // 'Twilio' removed — the integration is commented out in IntegrationsSection,
  // so searching for it would jump to a section that no longer lists it.
  // 'Lusha' / 'UpLead' removed for the same reason: they are enrichment
  // providers in the workflow catalog but have no card in the integration
  // catalog, so both were results that pointed at nothing.
  // Anchors are `integrations-<integration id>`, minted by IntegrationsSection
  // from the same ids (underscores normalised to hyphens).
  integrations: [
    kw('Apollo', 'integrations-apollo'),
    kw('Hunter', 'integrations-hunter'),
    kw('LinkedIn', 'integrations-linkedin'),
    kw('HubSpot', 'integrations-hubspot'),
    kw('Pipedrive', 'integrations-pipedrive'),
    kw('Slack', 'integrations-slack'),
    kw('Calendly', 'integrations-calendly'),
    kw('People Data Labs', 'integrations-peopledatalabs'),
    kw('Explorium', 'integrations-explorium'),
  ],
  // Three of these reuse AutoReplySection's own `autoreply-*` field ids rather
  // than adding a second attribute next to each one; only the sentiment Select,
  // which has no bindable id, carries an explicit anchor. The rule fields exist
  // only while the rule form is open — with it closed the panel opens at the top,
  // which is where the "New Rule" button that renders them lives.
  // Emptied with the hidden Auto-Reply Rules section — otherwise settings
  // search would still deep-link into a section that has no nav entry.
  // (Kept as a key, not deleted: SECTION_KEYWORDS is a total Record over
  // SettingsSection, and 'auto-reply' is still a member of that union.)
  'auto-reply': [
    // kw('Auto-reply rule', 'autoreply-rule-name'),
    // kw('Sentiment', 'autoreply-trigger-sentiment'),
    // kw('Keywords', 'autoreply-trigger-keywords'),
    // kw('Trigger', 'autoreply-trigger-sentiment'),
    // kw('Response template', 'autoreply-response-template'),
  ],
  // Campaign Defaults — hidden. The key stays (the Record is keyed on the
  // whole SettingsSection union); the terms go, so settings search cannot
  // route to a section that no longer renders.
  'campaign-defaults': [
    // kw('Email interval', 'campaign-defaults-email-interval'),
    // kw('Batch size', 'campaign-defaults-batch-size'),
    // kw('Follow-ups', 'campaign-defaults-follow-ups'),
    // kw('Daily send limit', 'campaign-defaults-daily-send-limit'),
  ],
  security: [
    kw('Change password', 'security-change-password'),
    // Both reuse the ids the password inputs already carry for their labels.
    // Absent on SSO accounts, which have no password to change here.
    kw('Current password', 'security-current-password'),
    kw('New password', 'security-new-password'),
    kw('API keys', 'security-api-keys'),
    kw('Sessions', 'security-sessions'),
    kw('Account status', 'security-account-status'),
  ],
  // Emptied with the hidden Custom AI Recipes section — otherwise settings
  // search would still deep-link into a section that has no nav entry, landing
  // the user on Account with no explanation.
  // (Kept as a key, not deleted: sectionKeywords is a total Record over
  // SettingsSection, and 'custom-ai-recipes' is still a member of that union.)
  'custom-ai-recipes': [
    // kw('Recipe', 'custom-ai-recipes-recipe-name'),
    // kw('Prompt', 'custom-ai-recipes-prompt'),
    // kw('Output schema', 'custom-ai-recipes-output-schema'),
    // kw('Model', 'custom-ai-recipes-model'),
    // kw('Custom context', 'custom-ai-recipes-custom-context'),
  ],
  team: [
    kw('Invite'),
    kw('Teammate'),
    kw('Role'),
    kw('Member'),
    kw('Owner'),
    kw('Admin'),
  ],
  'roles-access': [
    kw('Roles'),
    kw('Permissions'),
    kw('Access'),
    kw('Owner'),
    kw('Admin'),
    kw('Manager'),
    kw('Representative'),
    kw('Analyst'),
  ],
  'workflow-approvals': [
    kw('Workflow'),
    kw('Approval'),
    kw('Pending'),
    kw('Activate'),
  ],
  'campaign-approvals': [
    kw('Campaign approval'),
    kw('Launch request'),
    kw('Pending launch'),
  ],
  'crm-export-approvals': [
    kw('CRM export approval'),
    kw('Export request'),
    kw('Push contacts to CRM'),
  ],
  'deletion-approvals': [
    kw('Deletion approval'),
    kw('Delete request'),
    kw('Delete contacts'),
    kw('Delete list'),
  ],
  'my-approval-requests': [
    kw('My requests'),
    kw('Sent for approval'),
    kw('Approval status'),
    kw('Rejected'),
  ],
  'audit-log': [
    kw('Audit'),
    kw('Log'),
    kw('Activity'),
  ],
  // Anchors are `help-<module id>` / `help-<article id>` from
  // helpCentre/helpContent.ts; the panel opens on Support & FAQ, where they
  // live. The videos tab has no anchors (nothing to scroll to yet).
  'help-centre': [
    kw('Help'),
    kw('FAQ'),
    kw('Support'),
    kw('Guide videos'),
    kw('Getting started', 'help-getting-started'),
    kw('Import contacts', 'help-contacts-import-file'),
    kw('Node reference', 'help-workflows-node-reference'),
    kw('Launch a campaign', 'help-campaigns-launch'),
    kw('Warm up a mailbox', 'help-integrations-warmup'),
    kw('Credits', 'help-billing-credits-and-subscription'),
  ],
  'feedback-support': [
    kw('Report a bug'),
    kw('Bug report'),
    kw('Feature request'),
    kw('Rate the app'),
    kw('Rating'),
  ],
}

interface Match {
  id: SettingsSection
  label: string
  icon: Icon
  sub?: string // matched sub-item (keyword), if any
  anchor?: string // deep-link slug for the matched sub-item, if it has one
}

/** Highlight the matched substring within a string. */
function highlightMatch(text: string, q: string): React.ReactNode {
  const idx = text.toLowerCase().indexOf(q.toLowerCase())
  if (idx === -1 || !q) return text
  return (
    <>
      {text.slice(0, idx)}
      <span className="text-[#5e6ad2] dark:text-[#a5b4fc]">{text.slice(idx, idx + q.length)}</span>
      {text.slice(idx + q.length)}
    </>
  )
}

function buildMatches(q: string, items: typeof navItems): Match[] {
  if (!q) return []
  const out: Match[] = []
  const seen = new Set<string>()
  for (const item of items) {
    // Section-name match (no sub-item).
    if (item.label.toLowerCase().includes(q)) {
      const k = `${item.id}::`
      if (!seen.has(k)) { seen.add(k); out.push({ id: item.id, label: item.label, icon: item.icon }) }
    }
    // Sub-item / field matches.
    for (const entry of sectionKeywords[item.id] || []) {
      if (entry.label.toLowerCase().includes(q)) {
        const k = `${item.id}::${entry.label}`
        if (!seen.has(k)) {
          seen.add(k)
          out.push({ id: item.id, label: item.label, icon: item.icon, sub: entry.label, anchor: entry.anchor })
        }
      }
    }
  }
  return out.slice(0, 8)
}

const SettingsNav: React.FC<SettingsNavProps> = ({ activeSection, onSectionChange }) => {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [activeIdx, setActiveIdx] = useState(0)
  // readOnly until focus — blocks Chrome from autofilling this box as the
  // "username" for the password fields in the Security section (§9).
  const [readOnly, setReadOnly] = useState(true)
  const containerRef = useRef<HTMLDivElement>(null)

  // Rep has no PERMISSIONS entry for "auto_reply.manage" at all -- the nav
  // entry that leads to AutoReplySection must not be present for that role,
  // matching the section itself (which also hides for the same reason).
  const { allowed: autoReplyAllowed } = usePermission('auto_reply', 'manage')

  // Rep's own level for workflows.activate is "gated" (truthy -- "may
  // request activation of their own workflow"), never "may approve one" --
  // only "all" (Owner/Admin/Manager) may see the approvals queue at all.
  // Mirrors WorkflowApprovalsSection's own canApprove check.
  const { allowed: workflowsAllowed, scope: workflowsScope } = usePermission('workflows', 'activate')
  const canApproveWorkflows = workflowsAllowed && workflowsScope === 'all'

  // Same shape for campaigns: a Rep's campaigns.launch level is "gated"
  // (may request a launch), so only "all" may review the queue.
  const { allowed: campaignsLaunchAllowed, scope: campaignsLaunchScope } = usePermission('campaigns', 'launch')
  const canApproveCampaigns = campaignsLaunchAllowed && campaignsLaunchScope === 'all'

  // And again for CRM exports: a Rep's contacts.bulk_export level is "gated"
  // (may request an export of their own contacts), so only "all" may review.
  const { allowed: bulkExportAllowed, scope: bulkExportScope } = usePermission('contacts', 'bulk_export')
  const canApproveCrmExports = bulkExportAllowed && bulkExportScope === 'all'
  // Same shape: a Rep's "gated" hard_delete means "may request", never "may approve".
  const { allowed: hardDeleteAllowed, scope: hardDeleteScope } = usePermission('contacts', 'hard_delete')
  const canApproveDeletions = hardDeleteAllowed && hardDeleteScope === 'all'

  // Rep/Analyst have no PERMISSIONS["audit_log"]["view"] cell at all.
  const { allowed: auditLogAllowed } = usePermission('audit_log', 'view')

  // Same for integrations.manage / ai_models.manage: Owner/Admin "manage",
  // Manager "view" (read-only, section still opens), Rep/Analyst nothing --
  // so those two roles get no nav entry, matching the sections themselves.
  const { allowed: integrationsAllowed } = usePermission('integrations', 'manage')
  const { allowed: aiModelsAllowed } = usePermission('ai_models', 'manage')

  // sender_emails.manage is the mailbox inventory: Owner/Admin "manage",
  // Manager/Rep "view" (read-only, section still opens), Analyst nothing --
  // spec sect.4-D gives them no sender-email access at all (AB#22341). The
  // Analytics mailbox filter reads sender_emails.list_addresses instead, so
  // dropping this entry costs them nothing there.
  const { allowed: senderEmailsAllowed } = usePermission('sender_emails', 'manage')

  // signatures.manage: Owner/Admin/Manager "manage", Rep "own" (their own
  // rows, may still pick any at launch), Analyst nothing. The plan lock on the
  // library is the section's job, not the nav's.
  const { allowed: signaturesAllowed } = usePermission('signatures', 'manage')

  // Only a Representative ever submits for approval, so only a Representative
  // has a "My Approval Requests" list worth showing.
  const isRepresentative = useAuthStore((s) => s.user?.role) === 'representative'

  const visibleNavItems = navItems.filter((item) => {
    if (item.id === 'auto-reply') return autoReplyAllowed
    if (item.id === 'workflow-approvals') return canApproveWorkflows
    if (item.id === 'campaign-approvals') return canApproveCampaigns
    if (item.id === 'crm-export-approvals') return canApproveCrmExports
    if (item.id === 'deletion-approvals') return canApproveDeletions
    if (item.id === 'audit-log') return auditLogAllowed
    if (item.id === 'integrations') return integrationsAllowed
    if (item.id === 'ai-models') return aiModelsAllowed
    if (item.id === 'email-accounts') return senderEmailsAllowed
    if (item.id === 'signatures') return signaturesAllowed
    if (item.id === 'my-approval-requests') return isRepresentative
    return true
  })

  const q = query.trim().toLowerCase()
  const results = buildMatches(q, visibleNavItems)
  const showDropdown = open && q.length > 0

  useEffect(() => setActiveIdx(0), [q])

  useEffect(() => {
    if (!showDropdown) return
    const onClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [showDropdown])

  const select = (id: SettingsSection, anchor?: string) => {
    onSectionChange(id, anchor)
    setQuery('')
    setOpen(false)
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!showDropdown) return
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActiveIdx((i) => Math.min(i + 1, results.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActiveIdx((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (results[activeIdx]) select(results[activeIdx].id, results[activeIdx].anchor)
    } else if (e.key === 'Escape') {
      setOpen(false)
    }
  }

  return (
    <nav className="w-56 flex-shrink-0">
      <div className="sticky top-6 space-y-2">
        {/* Search */}
        <div ref={containerRef} className="relative">
          <MagnifyingGlass className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-muted-foreground dark:text-[#62666d] z-10" />
          <input
            type="search"
            name="settings-search"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true) }}
            onFocus={() => { setReadOnly(false); setOpen(true) }}
            onKeyDown={onKeyDown}
            placeholder="Search settings…"
            readOnly={readOnly}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck={false}
            data-1p-ignore
            data-lpignore="true"
            data-form-type="other"
            className="w-full h-9 pl-8 pr-3 bg-background dark:bg-white/[0.02] border border-border/60 dark:border-white/[0.08] hover:border-border/80 dark:hover:border-white/[0.14] rounded-md text-[13px] text-foreground dark:text-[#f7f8f8] placeholder:text-muted-foreground/50 dark:placeholder:text-[#62666d] focus:outline-none focus-visible:border-[#7170ff]/55 dark:focus-visible:border-[#7170ff]/75 transition-colors duration-150 [&::-webkit-search-cancel-button]:hidden"
          />

          {/* Floating results dropdown */}
          {showDropdown && (
            <div className="absolute left-0 right-0 top-full mt-1.5 z-50 rounded-lg border bg-card border-border/60 shadow-lg dark:bg-[#191a1b] dark:border-white/[0.08] overflow-hidden py-1">
              {results.length === 0 ? (
                <p className="px-3 py-3 text-center text-[12px] text-muted-foreground dark:text-[#62666d]">No settings found</p>
              ) : (
                results.map((m, i) => {
                  const ItemIcon = m.icon
                  return (
                    <button
                      key={`${m.id}-${m.sub ?? ''}`}
                      type="button"
                      onMouseEnter={() => setActiveIdx(i)}
                      onMouseDown={(e) => { e.preventDefault(); select(m.id, m.anchor) }}
                      className={cn(
                        'w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors duration-100',
                        i === activeIdx ? 'bg-[#5e6ad2]/[0.08] dark:bg-[#5e6ad2]/[0.12]' : 'hover:bg-muted/40 dark:hover:bg-white/[0.03]',
                      )}
                    >
                      <ItemIcon className="w-4 h-4 flex-shrink-0 text-muted-foreground dark:text-[#8a8f98]" weight="regular" />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
                          {m.sub ? m.label : highlightMatch(m.label, query.trim())}
                        </span>
                        {m.sub && (
                          <span className="block truncate text-muted-foreground dark:text-[#8a8f98]" style={{ fontSize: '11px', letterSpacing: '-0.05px' }}>
                            {highlightMatch(m.sub, query.trim())}
                          </span>
                        )}
                      </span>
                    </button>
                  )
                })
              )}
            </div>
          )}
        </div>

        {/* Full nav list — always visible */}
        <div className="space-y-0.5">
          {visibleNavItems.map((item) => {
            const isActive = activeSection === item.id
            const ItemIcon = item.icon
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onSectionChange(item.id)}
                className={cn(
                  'w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-left transition-colors duration-150',
                  isActive
                    ? 'bg-[#5e6ad2]/[0.10] dark:bg-[#5e6ad2]/[0.14]'
                    : 'hover:bg-muted/50 dark:hover:bg-white/[0.04]',
                )}
              >
                <ItemIcon
                  className={cn(
                    'w-4 h-4 flex-shrink-0',
                    isActive ? 'text-[#5e6ad2] dark:text-[#a5b4fc]' : 'text-muted-foreground dark:text-[#8a8f98]',
                  )}
                  weight={isActive ? 'fill' : 'regular'}
                />
                <span
                  className={cn(
                    'truncate',
                    isActive ? 'text-[#5e6ad2] dark:text-[#a5b4fc]' : 'text-foreground/80 dark:text-[#d0d6e0]',
                  )}
                  style={{ fontSize: '13px', fontWeight: isActive ? 590 : 510, letterSpacing: '-0.13px' }}
                >
                  {item.label}
                </span>
              </button>
            )
          })}
        </div>
      </div>
    </nav>
  )
}

export default SettingsNav
